import React, { useEffect, useState } from 'react';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../../hooks/useBrandingColors';
import { publicApiClient } from '../../api/apiClient';
import { loadCompaniesForSelect } from '../../services/companySelectOptionsService';
import { labelCls, CloseIcon } from './companyModalUi';
import {
  ROLE_OPTIONS,
  EMAIL_RE,
  PASSWORD_MIN,
  selectCls,
  inputCls,
  Chevron,
  roleLabel,
  accountErrorMessage,
} from './companyUserModalShared';

interface Props {
  /** Редактируемый пользователь (userId из строки таблицы сотрудников). */
  userId: number;
  onClose: () => void;
  /** Успешное сохранение — родитель закрывает модалку и перезагружает таблицу. */
  onSaved: () => void;
}

export const EditCompanyUserModal: React.FC<Props> = ({ userId, onClose, onSaved }) => {
  const colors = useBrandingColors();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(''); // пусто = не менять
  const [role, setRole] = useState<string>('RECRUITER');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([]);

  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Префил данными аккаунта + список компаний для дропдауна.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      publicApiClient.account.getAccountById(userId),
      loadCompaniesForSelect().catch(() => []),
    ])
      .then(([acc, comp]) => {
        if (cancelled) return;
        const a: any = acc.data || {};
        setFirstName(a.firstName ?? '');
        setLastName(a.lastName ?? '');
        setEmail(a.email ?? '');
        if (a.role) setRole(String(a.role));
        if (a.companyId != null) setSelectedCompanyId(Number(a.companyId));
        const list = ((comp as any[]) || []).map(c => ({ id: c.id as number, name: c.name as string }));
        setCompanies(list);
      })
      .catch((err: any) => {
        if (!cancelled) setError(accountErrorMessage(err, 'Не удалось загрузить пользователя'));
      })
      .finally(() => {
        if (!cancelled) setInitLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Если роль аккаунта вне списка назначаемых (напр. SUPER_ADMIN) — показываем её как текущую.
  const roleOptions = ROLE_OPTIONS.some(r => r.value === role)
    ? ROLE_OPTIONS
    : [{ value: role, label: roleLabel(role) }, ...ROLE_OPTIONS];

  const validate = (): string | null => {
    if (!firstName.trim()) return 'Введите имя';
    if (!lastName.trim()) return 'Введите фамилию';
    if (!email.trim()) return 'Введите почту';
    if (!EMAIL_RE.test(email.trim())) return 'Введите корректный email адрес';
    if (password && password.length < PASSWORD_MIN) return `Пароль должен содержать минимум ${PASSWORD_MIN} символов`;
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload: any = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        role: role as any,
      };
      if (selectedCompanyId != null) payload.companyId = selectedCompanyId;
      if (password) payload.password = password; // пустое — не меняем
      await publicApiClient.account.updateAccountById(userId, payload);
      onSaved();
    } catch (err: any) {
      setError(accountErrorMessage(err, 'Не удалось сохранить пользователя'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-[540px] max-w-[calc(100vw-32px)] max-h-[90vh] overflow-y-auto rounded-[24px] bg-[#F5F6F1]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
          {/* Заголовок */}
          <div className="flex items-center justify-between h-8">
            <span className="w-8 shrink-0" />
            <h2 className="flex-1 text-center text-[24px] leading-[32px] font-medium tracking-[-0.015em] text-black">
              Редактирование пользователя
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 shrink-0 flex items-center justify-center text-[#868C98] hover:text-[#0A0D14]"
              aria-label="Закрыть"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="flex flex-col gap-6">
            {/* Имя + Фамилия */}
            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls} htmlFor="edit-user-first-name">Имя</label>
                <input
                  id="edit-user-first-name"
                  type="text"
                  placeholder="Например: Иван"
                  value={firstName}
                  onChange={e => { setFirstName(e.target.value); setError(null); }}
                  className={inputCls}
                  disabled={initLoading}
                />
              </div>
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls} htmlFor="edit-user-last-name">Фамилия</label>
                <input
                  id="edit-user-last-name"
                  type="text"
                  placeholder="Например: Иванов"
                  value={lastName}
                  onChange={e => { setLastName(e.target.value); setError(null); }}
                  className={inputCls}
                  disabled={initLoading}
                />
              </div>
            </div>

            {/* Почта */}
            <div className="flex flex-col gap-3">
              <label className={labelCls} htmlFor="edit-user-email">Почта</label>
              <input
                id="edit-user-email"
                type="text"
                inputMode="email"
                placeholder="example@mail.ru"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(null); }}
                className={inputCls}
                disabled={initLoading}
              />
            </div>

            {/* Пароль (пусто = не менять) */}
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-col gap-3">
                <label className={labelCls} htmlFor="edit-user-password">Пароль</label>
                <input
                  id="edit-user-password"
                  type="text"
                  placeholder="Qwerty123!"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(null); }}
                  className={inputCls}
                  disabled={initLoading}
                />
              </div>
              <p className="text-[12px] leading-[20px] tracking-[-0.006em] text-[#868C98]">
                * Пароль должен быть надежным и содержать не менее {PASSWORD_MIN} символов.
              </p>
            </div>

            {/* Компания (full width) */}
            <div className="flex flex-col gap-3">
              <label className={labelCls} htmlFor="edit-user-company">Компания</label>
              <div className="relative">
                <select
                  id="edit-user-company"
                  value={selectedCompanyId ?? ''}
                  onChange={e => setSelectedCompanyId(Number(e.target.value))}
                  className={`${selectCls} truncate`}
                  disabled={initLoading}
                >
                  {selectedCompanyId != null && !companies.some(c => c.id === selectedCompanyId) && (
                    <option value={selectedCompanyId}>Текущая компания</option>
                  )}
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <Chevron />
              </div>
            </div>

            {/* Роль (full width) */}
            <div className="flex flex-col gap-3">
              <label className={labelCls} htmlFor="edit-user-role">Роль</label>
              <div className="relative">
                <select
                  id="edit-user-role"
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  className={selectCls}
                  disabled={initLoading}
                >
                  {roleOptions.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <Chevron />
              </div>
            </div>

            {error && <p className="text-[14px] text-[#DF1C41]" role="alert">{error}</p>}
          </div>

          {/* Кнопки */}
          <div className="flex justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="w-[113px] h-[52px] rounded-[12px] flex items-center justify-center bg-[#E2E4E9] text-[#525866] text-[20px] leading-7 font-medium hover:bg-[#d8dade] transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading || initLoading}
              className="w-[143px] h-[52px] rounded-[12px] flex items-center justify-center text-white text-[20px] leading-7 font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={getButtonInlineStyles(colors, 'primary')}
              onMouseEnter={e => { if (!loading && !initLoading) Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary')); }}
              onMouseLeave={e => { if (!loading && !initLoading) Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary')); }}
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCompanyUserModal;
